import { useState, useEffect, useRef } from 'react';
import type { Flight } from '../types';
import { getFlightMatchCounts } from '../services/supabaseService';

/**
 * Fetches anonymous flight match counts for a set of flights.
 * Returns a Map of "flightNumber|date" -> count (only entries with count > 0).
 */
export function useFlightMatchCounts(
  flights: Flight[],
  month: number,
  year: number,
): Map<string, number> {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const cacheRef = useRef<Map<string, Map<string, number>>>(new Map());

  useEffect(() => {
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;

    if (cacheRef.current.has(monthKey)) {
      setCounts(cacheRef.current.get(monthKey)!);
      return;
    }

    const monthFlights = flights.filter((f) => {
      const d = new Date(f.date + 'T00:00:00');
      return d.getMonth() + 1 === month && d.getFullYear() === year;
    });

    if (monthFlights.length === 0) {
      setCounts(new Map());
      return;
    }

    // Deduplicate — same flight number + date can appear in turnarounds
    const seen = new Set<string>();
    const flightNumbers: string[] = [];
    const dates: string[] = [];
    for (const f of monthFlights) {
      const key = `${f.flightNumber}|${f.date}`;
      if (seen.has(key)) continue;
      seen.add(key);
      flightNumbers.push(f.flightNumber);
      dates.push(f.date);
    }

    let cancelled = false;

    getFlightMatchCounts(flightNumbers, dates).then((results) => {
      if (cancelled) return;
      const map = new Map<string, number>();
      for (const r of results) {
        if (r.count > 0) {
          map.set(`${r.flightNumber}|${r.date}`, r.count);
        }
      }
      cacheRef.current.set(monthKey, map);
      setCounts(map);
    });

    return () => { cancelled = true; };
  }, [flights, month, year]);

  return counts;
}
